// Patch nectar meter (flower-attach-meter, playtest finding 11): a bar on each patch showing
// `pool / 255`, shown ONLY while the patch is not full, so a healthy field stays visually
// quiet and depletion is what draws the eye. Pure and Pixi-free, mirroring
// `ground-scatter.ts`'s zoom-suppression shape (`scatterDetailForScale`); the batched
// `Graphics` redraw lives in renderer.ts (the `danceOverlay`/`hiveCellOverlay` pattern —
// one `Graphics` layer redrawn per frame, never one per patch), exercised on-device only
// (repo convention D-086).

import type { PatchRecord } from '../net/wire/messages';

/** The quantization ceiling of `PatchRecord.pool` (wire-entity-detail): 255 = full, matching
 * `flowerSheetForPatch`'s `pool > 0` live/dead boundary at the other end of the range. */
export const PATCH_POOL_MAX = 255;

/** The authored on-screen width (px) of the meter bar at camera scale 1 — the single source
 * of truth for both the legibility decision below and the actual `renderer.ts` paint width,
 * mirroring `ground-scatter.ts`'s `SCATTER_SPRITE_PX`. */
export const METER_BAR_WIDTH_PX = 12;

/** Below this on-screen bar width (px) the meter would read as sub-pixel noise rather than a
 * legible bar, so it is suppressed outright — the same zoom-suppression idea as
 * `ground-scatter.ts`'s `SCATTER_MIN_LEGIBLE_PX`, applied to the patch meter. */
const METER_MIN_LEGIBLE_PX = 4;

/** The minimal per-patch shape the meter needs — a structural subset of `PatchRecord`, so a
 * test fixture (or a future non-wire caller) need not build a full record. */
export type MeterPatch = Pick<PatchRecord, 'id' | 'position' | 'pool'>;

/** The render modes over the two wire tiers (UI.md §8), mirroring `hive-cells.ts`'s
 * `HiveCellRenderMode`: `full`/`reduced` (close tier) render identical meter geometry — the
 * meter is static, not animated per RAF — and `none` (wide tier) renders no per-patch meter
 * at all (the wide tier's own aggregate `bloom` marker stands in for it). */
export type PatchMeterRenderMode = 'full' | 'reduced' | 'none';

/** One patch's meter draw geometry: which patch, where, and how full. */
export interface PatchMeterGeometry {
  readonly patchId: number;
  readonly position: readonly [number, number];
  /** Fill fraction in [0, 1] — `pool / PATCH_POOL_MAX`. */
  readonly fraction: number;
}

/** The fill fraction in [0, 1] for a raw `pool` byte — `pool / 255`, clamped defensively
 * against a value outside the wire's documented 0..255 quantization range. */
export function meterFraction(pool: number): number {
  return Math.max(0, Math.min(1, pool / PATCH_POOL_MAX));
}

/** Whether the patch meter is legible at all at camera `scale` (flower-attach-meter): below
 * this, the bar is suppressed regardless of any patch's fullness, so a wide-zoom field of
 * hundreds of patches stays visually quiet rather than carpeted in speckled bars. Total over
 * any finite input — a non-finite or non-positive scale is defensively illegible. */
export function meterLegibleAtScale(scale: number): boolean {
  return Number.isFinite(scale) && scale > 0 && scale * METER_BAR_WIDTH_PX >= METER_MIN_LEGIBLE_PX;
}

/**
 * One patch's meter geometry, or `undefined` when it should not render at all (flower-attach-
 * meter finding 11): a full patch (`pool >= PATCH_POOL_MAX`) shows no bar — a healthy field
 * stays quiet and depletion is what draws the eye. A patch at `pool === 0` still returns
 * geometry at `fraction: 0` (an empty bar), consistent with `flowerSheetForPatch` swapping to
 * the dead-flower sheet at the same threshold — the bar and the sprite swap never disagree.
 */
export function patchMeterFor(patch: MeterPatch): PatchMeterGeometry | undefined {
  if (patch.pool >= PATCH_POOL_MAX) {
    return undefined;
  }
  return { patchId: patch.id, position: patch.position, fraction: meterFraction(patch.pool) };
}

/**
 * The meters to draw this frame: every non-full patch's geometry, or none at all when `mode`
 * is `none` (the wide tier — mirrors `hiveCellDrawables`) or `cameraScale` makes the bar
 * sub-legible ({@link meterLegibleAtScale}). `full` and `reduced` produce identical output —
 * the meter is static geometry, not animated per RAF. The caller batches these into a single
 * `Graphics` redraw (renderer.ts's `danceOverlay`/`hiveCellOverlay` pattern) — never one
 * `Graphics` instance per patch per frame, which would not scale to the 600-patch seed field
 * (DESIGN §14).
 */
export function patchMeterDrawables(
  patches: Iterable<MeterPatch>,
  cameraScale: number,
  mode: PatchMeterRenderMode,
): PatchMeterGeometry[] {
  if (mode === 'none' || !meterLegibleAtScale(cameraScale)) {
    return [];
  }
  const out: PatchMeterGeometry[] = [];
  for (const patch of patches) {
    const geom = patchMeterFor(patch);
    if (geom !== undefined) {
      out.push(geom);
    }
  }
  return out;
}
